import { Chip, ChipGroup } from '@src/components/common/ChipGroup';
import { PrimaryButton } from '@src/components/common/dialogStyles';
import { useKaraoke } from '@src/hooks/useKaraoke';
import { useKaraokeStore, useUserIdentity } from '@src/stores-v2';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import EditSparkleIcon from '@src/svgs/EditSparkleIcon';
import { analytics } from '@src/utility/analytics';
import {
  Bold,
  BoldValues,
  getCustomFontFromPostScriptName,
} from '@src/utility/fonts';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import BackIcon from '../../../svgs/BackIcon';
import { KaraokeCard } from '../../../types.ts/general';
import SpinningLoading from '../../SpinningLoading';
import GlobalKaraokePresetSelect from '../GlobalKaraokePresetSelect';
import { SettingsPreviewSection } from '../SettingsPreviewSection';
import { CaptionsPreviewThumbnail } from '../VideoTemplateSettings/CaptionsPreviewThumbnail';
import KaraokeSaveOrCancelTemplate from './KaraokeSaveOrCancelTemplate';
import KaraokeTemplateSelect from './KaraokeTemplateSelect';
import { resolveCaptionFont } from './resolveCaptionFont';
import {
  Action,
  AlignValues,
  Readability,
  TextSettingsUI,
} from './TextSettingsUI';

import type { Align } from './TextSettingsUI';
import type { AnalyticsBrowser } from '@segment/analytics-next';
import type { TextBrandingService } from '@src/components/textProcessor/TextBrandingService';
import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { ElementState } from '@src/renderer/ElementState';
import type KaraokeProducer from '@src/services/karaoke/KaraokeProducer';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

type Props = {
  activeElement: ElementState | undefined;
  fallbackKaraokeConfig: KaraokeConfig;
  editorStore: EditorStoreLike;
};

/**
 * Reusable function to produce karaoke text with all necessary dependencies passed in.
 * This function handles analytics tracking, karaoke generation, and template persistence.
 *
 * @param newConfig - Optional karaoke configuration to use. If not provided, generates default config.
 * @param dependencies - Object containing all necessary dependencies for karaoke production
 */
export const produceKaraokeTextCore = async (
  newConfig: KaraokeConfig | undefined,
  dependencies: {
    analytics: AnalyticsBrowser;
    videoCreator: IVideoCreatorStore;
    config: KaraokeConfig;
    isKaraokeGenerated: boolean;
    karaokeProducer: KaraokeProducer;
    textBrandingService: TextBrandingService;
    setDefaultKaraokeConfigId: (id: string) => void;
    updateKaraokeConfigAndProduce: (
      config: KaraokeConfig,
      createUndoRedoEntry?: boolean,
    ) => void;
    commandProduceKaraoke: (options?: {
      replaceTrack: boolean;
      createUndoRedoEntry?: boolean;
    }) => void;
  },
) => {
  const {
    analytics,
    videoCreator,
    config,
    isKaraokeGenerated,
    karaokeProducer,
    textBrandingService,
    setDefaultKaraokeConfigId,
    updateKaraokeConfigAndProduce,
    commandProduceKaraoke,
  } = dependencies;

  analytics.track('karaoke_text_generated', {
    config: newConfig,
    storyId: videoCreator.story?.id,
    storyTitle: videoCreator.story?.title,
    videoId: videoCreator.currentVideo?.id,
    videoTitle: videoCreator.currentVideo?.title,
  });

  try {
    if (newConfig) {
      setDefaultKaraokeConfigId(newConfig.id);
      // Don't create undo entry here - applyTemplate will create the single undo entry
      updateKaraokeConfigAndProduce(newConfig, false);
    } else {
      // PLAT-11877 — a FIRST produce takes the showcase's default caption
      // template (`getDefaultCaptionTemplateConfig`, the one rule every
      // surface uses), not the built-in "Impact" look; the sidebar then marks
      // that template's card. With no template the built-in settings apply
      // as before. A re-produce keeps the config it has.
      const defaultTemplate = isKaraokeGenerated
        ? undefined
        : textBrandingService.getDefaultCaptionTemplateConfig(
            videoCreator.getCurrentAspectRatio(),
          );
      if (defaultTemplate) {
        newConfig = {
          ...config,
          ...defaultTemplate.config,
          id: config.id,
          language: config.language,
          parent_template_id: defaultTemplate.templateId,
        };
        textBrandingService.selectedTemplateId = defaultTemplate.templateId;
        setDefaultKaraokeConfigId(newConfig.id);
        updateKaraokeConfigAndProduce(newConfig, false);
      } else {
        newConfig = {
          ...config,
          ...(!isKaraokeGenerated &&
            karaokeProducer.getKaraokeTextSettingByAspectRatio()),
        };
        setDefaultKaraokeConfigId(newConfig.id);
        // Don't create undo entry here - applyTemplate will create the single undo entry
        commandProduceKaraoke({
          replaceTrack: true,
          createUndoRedoEntry: false,
        });
      }
    }
    videoCreator.setShowSubtitles(true);

    // Save karaokeTemplateId to extraElementData when producing karaoke
    const templateId = textBrandingService.selectedTemplateId || 'default';
    textBrandingService.saveToExtraElementData('karaoke', templateId);
    if (!isKaraokeGenerated && !textBrandingService.selectedTemplateId) {
      textBrandingService.selectedTemplateId = 'default';
    }
  } catch (e) {
    console.error('Error generating karaoke text:', e);
  } finally {
    videoCreator.karaokeLoading = false;
  }
};

/**
 * KaraokeEditorView Component
 *
 * Main karaoke editing interface with clearly separated dependencies for easier refactoring.
 *
 * DEPENDENCIES:
 * =============
 * 1. videoCreator (VideoCreatorStore) - Main store instance
 *    - isKaraokeGenerated: boolean
 *    - karaokeSubMenu: KaraokeCard enum
 *    - karaokeLoading: boolean
 *    - isTimelineLoading: boolean
 *    - renderer: object
 *    - story: object
 *    - currentVideo: object
 *    - organization: object
 *    - setShowSubtitles(boolean): void
 *
 * 2. videoCreator.karaokeProducer - Karaoke generation service
 *    - getKaraokeConfig(): KaraokeConfig
 *    - getKaraokeTextSettingByAspectRatio(): Partial<KaraokeConfig>
 *    - rerenderWithNewConfig(config: KaraokeConfig): void
 *    - produceKaraoke(config: KaraokeConfig): Promise<void>
 *    - getClipByText(text: string): Clip | undefined
 *
 * 3. videoCreator.textBrandingService - Template management service
 *    - isTemplateModified: boolean
 *    - selectedTemplateId: string | undefined
 *    - brandingTemplates: Template[]
 *    - isLoading: boolean
 *    - isPropsChanged: boolean
 *    - prepareTemplateToSave: boolean
 *    - checkPropsChanged(type: string, props: object): void
 *    - saveToExtraElementData(type: string, templateId: string): void
 *    - getAllBrandingTemplates(type: string, aspectRatio: string, hasActiveElement: boolean): void
 *    - isPresetTemplate(templateId: string | undefined): boolean
 *
 * STATE:
 * ======
 * - addNewTemplate: boolean
 * - templateName: string | undefined
 * - templateNameError: boolean
 * - allowCustomTemplateSave: boolean
 * - selectedActions: (Action | Readability)[]
 */
const KaraokeEditorView = observer(
  forwardRef<HTMLDivElement, Props>(
    ({ activeElement, fallbackKaraokeConfig, editorStore }, ref) => {
      // ========================================
      // DEPENDENCIES
      // ========================================
      const videoCreator = useVideoCreatorStore();
      const karaokeProducer = videoCreator.karaokeProducer;
      const textBrandingService = videoCreator.textBrandingService;
      const karaokeStore = useKaraokeStore();
      const userIdentity = useUserIdentity();
      const { creatorStudioCaptionsEnableArborTemplates } = useFlagsCombination(
        userIdentity.currentRole,
      );
      const {
        getKaraokeConfigForKaraokeClipId,
        setDefaultKaraokeConfigId,
        updateKaraokeConfigAndProduce,
        commandProduceKaraoke,
      } = useKaraoke(editorStore.editor);

      // ========================================
      // DERIVED STATE FROM DEPENDENCIES
      // ========================================
      const isKaraokeGenerated = karaokeStore.hasKaraokeLines;

      const activeKaraokeElement = activeElement
        ? karaokeStore.karaokeLines.find(
            line => line.id === activeElement.source.id,
          )
        : null;

      const config =
        (activeKaraokeElement
          ? getKaraokeConfigForKaraokeClipId(activeKaraokeElement.id)
          : karaokeStore.defaultKaraokeConfig) ?? fallbackKaraokeConfig;

      const brandCustomFonts =
        videoCreator.organization?.branding?.customFonts || [];

      // ========================================
      // LOCAL STATE
      // ========================================
      const [addNewTemplate, toggleAddNewTemplate] = useState<boolean>(false);
      const [templateName, setTemplateName] = useState<string>();
      const [templateNameError, setTemplateNameError] =
        useState<boolean>(false);
      const [allowCustomTemplateSave, setAllowCustomTemplateSave] =
        useState<boolean>(false);
      const [selectedActions, setSelectedActions] = useState<
        (Action | Readability)[]
      >([Action.align]);
      const [selectedTemplateTab, setSelectedTemplateTab] = useState<
        'my-templates' | 'arbor-templates'
      >('my-templates');

      // ========================================
      // COMPUTED VALUES
      // ========================================
      const selectedShadow = useMemo(
        () =>
          config?.shadow_color
            ? {
                color: config.shadow_color as string,
                blur: config.shadow_blur as number,
                x: config.shadow_x as number,
                y: config.shadow_y as number,
              }
            : undefined,
        [config],
      );

      const hasStickyBottom =
        addNewTemplate ||
        allowCustomTemplateSave ||
        textBrandingService.isTemplateModified;

      const fontFamily = config.font_family;
      const fontWeight = config.font_weight;

      const selectedBold = useMemo(() => {
        const customFont = getCustomFontFromPostScriptName(
          fontFamily,
          brandCustomFonts,
        );
        if (customFont) return customFont.fontSubfamily;
        return Object.keys(BoldValues).find(
          (k: any) => BoldValues[k as Bold] === Number(fontWeight),
        ) as Bold;
      }, [brandCustomFonts, fontFamily, fontWeight]);

      const templates = (textBrandingService.brandingTemplates || []).filter(
        t => {
          const templateId = typeof t.id === 'string' ? t.id : undefined;
          return !textBrandingService.isPresetTemplate(templateId);
        },
      );

      // ========================================
      // HELPER FUNCTIONS
      // ========================================
      function updateSelectedActionsForReadability(
        newConfig: any,
        selectedActions: (Action | Readability)[],
      ) {
        for (const action in Readability) {
          if (Object.prototype.hasOwnProperty.call(Readability, action)) {
            const readabilityAction =
              Readability[action as keyof typeof Readability];

            if (
              newConfig[`hide${readabilityAction}` as keyof typeof newConfig] &&
              !selectedActions.includes(readabilityAction)
            ) {
              selectedActions.push(readabilityAction);
            } else if (
              !newConfig[
                `hide${readabilityAction}` as keyof typeof newConfig
              ] &&
              selectedActions.includes(readabilityAction)
            ) {
              selectedActions = selectedActions.filter(
                action => action !== readabilityAction,
              );
            }
          }
        }
      }

      // ========================================
      // CORE ACTIONS (DEPENDENCIES INTERACTION)
      // ========================================
      const modifyProperty = async (propertyMap: Partial<KaraokeConfig>) => {
        const newConfig = { ...config, ...propertyMap };

        textBrandingService.checkPropsChanged('karaoke', propertyMap);
        updateKaraokeConfigAndProduce(newConfig);
      };

      const setAnimation = (
        time: string | number | null,
        type: any,
        duration: number | null = null,
        background_effect: string | null = null,
      ) => {
        const isInstagram = type === 'instagram';
        // Remove existing animation from list
        const newAnimations =
          config.animations?.filter((keyframe: any) =>
            [0, 'start'].includes(time!)
              ? ![0, 'start'].includes(keyframe.time)
              : keyframe.time !== time,
          ) ?? [];

        if (type !== 'none') {
          if (isInstagram) {
            type = 'text-appear';
          }
          const animation: any = { type };

          if (time) {
            animation.time = time;
            animation.duration = duration || 1;
          }

          if (background_effect) {
            animation.background_effect = background_effect;
          }

          // Reverse animation when used as exit animation
          if (time === 'end') {
            animation.reversed = true;
          }

          newAnimations.push(animation);
        }
        const newConfig: KaraokeConfig = {
          ...config,
          // TODO instagram effect support needed?
          // instagramEffect: isInstagram,
          // ...(!config.instagramEffect &&
          //   isInstagram && {
          //     hideComma: true,
          //     hidePeriod: true,
          //   }),
          language: isInstagram ? 'original' : config.language,
          animations: newAnimations,
        };
        textBrandingService.checkPropsChanged('karaoke', {
          animations: newAnimations,
        });
        updateKaraokeConfigAndProduce(newConfig);
      };

      const produceKaraokeText = async (newConfig?: KaraokeConfig) => {
        karaokeStore.setIsKaraokeLoading(true);
        await produceKaraokeTextCore(newConfig, {
          analytics,
          videoCreator,
          config,
          isKaraokeGenerated,
          karaokeProducer,
          textBrandingService,
          setDefaultKaraokeConfigId,
          updateKaraokeConfigAndProduce,
          commandProduceKaraoke,
        });
      };

      // ========================================
      // UI ACTION HANDLERS
      // ========================================
      const toggleAction = (action: Action) => {
        const hasAction = selectedActions.find(a => a === action);
        let updatedActions = [...selectedActions];
        if (hasAction) {
          updatedActions = updatedActions.filter(a => a !== action);
        } else {
          updatedActions = [...updatedActions, action];
        }
        setSelectedActions(updatedActions);
      };

      const handleToggleAction = (
        action: Action,
        propertyName: keyof KaraokeConfig,
        normalState: string,
        requiredState: string,
      ) => {
        toggleAction(action);
        const isSelected = selectedActions.includes(action);
        const state = isSelected ? normalState : requiredState;
        modifyProperty({ [propertyName]: state });
      };

      const handleSanitizePunctuation = async (action: Readability) => {
        const newConfig = { ...config };
        if (action === Readability.comma) {
          newConfig.hideComma = !config.hideComma;
        }
        if (action === Readability.period) {
          newConfig.hidePeriod = !config.hidePeriod;
        }
        if (action === Readability.filler) {
          newConfig.hideFillers = !config.hideFillers;
        }
        await produceKaraokeText(newConfig);
      };

      const toggleAlign = (align: Align) => {
        const modifications = {
          x_alignment: AlignValues[align],
          x: '50%', // default position to keep it visible
        };
        modifyProperty(modifications);
      };

      const handleSetBold = (
        bold: string,
        fontWeight: number,
        fontScriptName?: string,
      ) => {
        const state = selectedBold === bold ? null : bold;
        const value = state ? fontWeight : BoldValues[Bold.normal];
        modifyProperty({
          font_weight: value.toString(),
          ...(fontScriptName && { font_family: fontScriptName }),
        });
      };

      // PLAT-12100 DS2 — the resolution is shared with the captions bar.
      const handleSetFont = (
        fontScriptName?: string,
        variant?: { weight: number; style?: 'normal' | 'italic' },
      ) => {
        modifyProperty(
          resolveCaptionFont(
            fontScriptName,
            variant,
            config.font_weight,
            brandCustomFonts,
          ),
        );
      };

      const handleAddTemplateButtonClick = async () => {
        if (!isKaraokeGenerated) {
          await produceKaraokeText();
        }
        textBrandingService.isTemplateModified = false;
        textBrandingService.prepareTemplateToSave = true;
        videoCreator.karaokeSubMenu = KaraokeCard.styles;
        toggleAddNewTemplate(true);
        setAllowCustomTemplateSave(true);
      };

      // ========================================
      // EFFECTS
      // ========================================
      useEffect(() => {
        const selectedActions: (Action | Readability)[] = [];
        if (config.font_style === 'italic') {
          selectedActions.push(Action.italic);
        }
        if (config.text_transform === 'uppercase') {
          selectedActions.push(Action.capitalize);
        }
        updateSelectedActionsForReadability(config, selectedActions);
        setSelectedActions(selectedActions);
      }, [config]);

      useEffect(() => {
        return () => {
          if (textBrandingService.isTemplateModified) {
            textBrandingService.isTemplateModified = false;
          }
        };
      }, []);

      const aspectRatio = videoCreator.getCurrentAspectRatio();

      // Ensure karaoke templates are loaded when navigating to the Templates/Styles tab
      useEffect(() => {
        if (
          !textBrandingService.isTemplateTypeLoaded('karaoke', aspectRatio) &&
          !textBrandingService.isLoading
        ) {
          const hasActiveElement = !!activeElement;
          textBrandingService.getAllBrandingTemplates(
            'karaoke',
            aspectRatio,
            hasActiveElement,
            true, // we don't want to apply some template here, just load
          );
        }
      }, [aspectRatio, activeElement, textBrandingService]);

      // ========================================
      // RENDER HELPERS
      // ========================================
      const alignType = Object.keys(AlignValues).find(
        k => AlignValues[k as Align] === config.x_alignment,
      ) as Align;

      const karaokeLabel = textBrandingService.prepareTemplateToSave
        ? 'Custom'
        : videoCreator.textBrandingService.selectedBrandingTemplate?.title
          ? videoCreator.textBrandingService.selectedBrandingTemplate.title.toString()
          : 'Custom';

      const renderStylesTabContent = () =>
        isKaraokeGenerated && (
          <div
            style={{ gap: '10px', display: 'flex', flexDirection: 'column' }}
          >
            <SettingsHeader>
              <TitleRow>
                <BackButton
                  onClick={() =>
                    (videoCreator.karaokeSubMenu = KaraokeCard.templates)
                  }
                >
                  <BackIcon width="14" height="10" strokeColor="currentColor" />
                </BackButton>
                <SettingsTitle>{karaokeLabel}</SettingsTitle>
              </TitleRow>
            </SettingsHeader>
            <SettingsPreviewSection label={karaokeLabel}>
              <CaptionsPreviewThumbnail videoCreator={videoCreator} />
            </SettingsPreviewSection>
            <TextSettingsUI
              ref={ref}
              skipFields={['text']}
              karaokeElement={config}
              selectedBold={selectedBold}
              selectedFontWeight={fontWeight}
              selectedActions={selectedActions}
              selectedFontFamily={fontFamily}
              selectedShadow={selectedShadow}
              modifyProperty={modifyProperty}
              handleSetFont={handleSetFont}
              handleSetBold={handleSetBold}
              handleToggleAction={handleToggleAction}
              handleSanitizePunctuation={handleSanitizePunctuation}
              hasStickyBottom={hasStickyBottom}
              alignType={alignType}
              toggleAlign={toggleAlign}
              setAnimation={setAnimation}
              animations={
                config.animations?.reduce<{
                  enter?: string;
                  exit?: string;
                }>((acc, animation) => {
                  const { time, type } = animation as {
                    time?: 'start' | 'end' | number;
                    type?: string;
                  };
                  if (!time || !type) {
                    return acc;
                  }
                  if (time === 'start' || time === 0) {
                    acc.enter = type;
                  } else if (time === 'end') {
                    acc.exit = type;
                  }
                  return acc;
                }, {}) || {}
              }
              editorStore={editorStore}
              activeElement={undefined} // on purpose
            />
          </div>
        );

      const excludeFromGlobalCaptions =
        videoCreator.organization?.customerType?.excludeFromGlobalCaptions ??
        false;

      const renderTemplatesTabContent = () => {
        // Customer type opts out of global captions: show only "Workspace Templates"
        // content, no tab strip, no Arbor Templates surface.
        if (excludeFromGlobalCaptions) {
          return (
            <KaraokeTemplateSelect
              config={config}
              onAddTemplateButtonClick={handleAddTemplateButtonClick}
              templates={templates}
              produceKaraokeText={produceKaraokeText}
            />
          );
        }
        return (
          <>
            {creatorStudioCaptionsEnableArborTemplates ? (
              <>
                <TemplateTabs>
                  <TemplateTab
                    isSelected={selectedTemplateTab === 'my-templates'}
                    onClick={() => setSelectedTemplateTab('my-templates')}
                  >
                    Workspace Templates
                  </TemplateTab>
                  <TemplateTab
                    isSelected={selectedTemplateTab === 'arbor-templates'}
                    onClick={() => setSelectedTemplateTab('arbor-templates')}
                  >
                    Arbor Templates
                  </TemplateTab>
                </TemplateTabs>
                {selectedTemplateTab === 'my-templates' ? (
                  <KaraokeTemplateSelect
                    config={config}
                    onAddTemplateButtonClick={handleAddTemplateButtonClick}
                    templates={templates}
                    produceKaraokeText={produceKaraokeText}
                  />
                ) : (
                  <GlobalKaraokePresetSelect
                    produceKaraokeText={produceKaraokeText}
                    config={config}
                  />
                )}
              </>
            ) : (
              <>
                <KaraokeTemplateSelect
                  config={config}
                  onAddTemplateButtonClick={handleAddTemplateButtonClick}
                  templates={templates}
                  produceKaraokeText={produceKaraokeText}
                />
                <GlobalKaraokePresetSelect
                  produceKaraokeText={produceKaraokeText}
                  config={config}
                />
              </>
            )}
          </>
        );
      };

      const renderCurrentTabContent = () => {
        // When karaoke is not generated, always show templates regardless of submenu
        if (!isKaraokeGenerated) {
          return renderTemplatesTabContent();
        }
        switch (videoCreator.karaokeSubMenu) {
          case KaraokeCard.templates:
            return renderTemplatesTabContent();
          default:
            return renderStylesTabContent();
        }
      };

      // ========================================
      // MAIN RENDER
      // ========================================
      return (
        <Main data-testid="captions-panel">
          {videoCreator.renderer?.ready && (
            <>
              {renderCurrentTabContent()}
              {isKaraokeGenerated &&
                videoCreator.karaokeSubMenu !== KaraokeCard.templates && (
                  <KaraokeSaveOrCancelTemplate
                    config={config}
                    templates={templates}
                    addNewTemplate={addNewTemplate}
                    setTemplateName={setTemplateName}
                    setTemplateNameError={setTemplateNameError}
                    templateName={templateName}
                    templateNameError={templateNameError}
                    toggleAddNewTemplate={toggleAddNewTemplate}
                    allowCustomTemplateSave={allowCustomTemplateSave}
                    setAllowCustomTemplateSave={setAllowCustomTemplateSave}
                    type="karaoke"
                    setting="karaoke-settings"
                  />
                )}
              {/* Show Generate button only on styles tab when no karaoke exists (edge case) */}
              {videoCreator.karaokeSubMenu !== KaraokeCard.templates &&
                (!karaokeStore.hasKaraokeLines || !isKaraokeGenerated) && (
                  <GenerateButton onClick={() => produceKaraokeText()}>
                    <EditSparkleIcon
                      width="16"
                      height="16"
                      strokeColor="currentColor"
                    />
                    Generate Captions
                  </GenerateButton>
                )}
              {videoCreator.karaokeLoading &&
                !textBrandingService.isPropsChanged && (
                  <SpinningLoading
                    text={
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>Generating karaoke text…</span>
                      </div>
                    }
                  />
                )}
              {videoCreator.isTimelineLoading && (
                <SpinningLoading
                  text={
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>Updating timeline…</span>
                    </div>
                  }
                />
              )}
            </>
          )}
        </Main>
      );
    },
  ),
);

export default KaraokeEditorView;

// ========================================
// STYLED COMPONENTS
// ========================================
const Main = styled.div`
  padding-top: 16px;
  /* No bottom spacing here: the panel card's Body already contributes
     --spacing-md, which is the whole of what the Speakers panel uses and is
     the pattern every tab now follows. Adding more here double-counted it. */
  /* Deliberately NOT a scroll container. This used to set height: 100% plus
     overflow-y: auto, making it a nested scroller inside the panel card's Body —
     so its scrollbar sat inside the Body's 16px padding rather than at the card
     edge, and scrollbar-gutter: stable both-edges reserved a matching gutter on
     the left, pushing the content inward on both sides. The Templates tab has no
     overflow of its own and lets the Body scroll, which puts the bar at the card
     edge; this now matches it. */
  padding-left: var(--spacing-sm);
  padding-right: var(--spacing-sm);
  box-sizing: border-box;
`;

const SettingsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius);
  padding: 4px;
  cursor: pointer;
  transition: all 150ms ease-out;
  color: var(--text-muted);

  &:hover {
    background-color: var(--surface-elevated);
    color: var(--foreground);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const SettingsTitle = styled.div`
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  letter-spacing: normal;
`;

const TemplateTabs = styled(ChipGroup)``;

const TemplateTab = styled(Chip)``;

/**
 * Was a styled.div at 48px with bold text, --radius and no hover state — the
 * same off-standard shape the Produce button had. PrimaryButton supplies
 * --btn-h-lg (40px), semi-bold, --radius-button, the hover it lacked, and real
 * <button> semantics.
 */
const GenerateButton = styled(PrimaryButton)`
  width: 100%;
  margin-top: var(--spacing-sm);
`;
